import {
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Heading,
  Link,
  Select,
  Separator,
  Text,
  TextField,
} from '@radix-ui/themes'
import {
  AlertCircle,
  AtSign,
  Camera,
  Facebook,
  Globe,
  Image,
  Instagram,
  Mail,
  MapPinHouse,
  Music2,
  Phone,
  User,
  Youtube,
} from 'lucide-react'
import { SubmitEvent } from 'react'
import { MediaGroupFormData } from '@/types/shared'

interface MediaGroupFormProps {
  data: MediaGroupFormData
  setData: <K extends keyof MediaGroupFormData>(key: K, value: MediaGroupFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void
  processing: boolean
  errors?: string[]
  title: string
  /**
   * Sits under the title. Defaults to the first-time setup pitch — the apply
   * flow shows these same fields as a last look before submitting, so it says
   * that instead.
   */
  subtitle?: string
  submitLabel: string
  /** Null leaves Cancel out — onboarding has its own Back and Skip. */
  cancelPath?: string | null
  currentLogoUrl?: string | null
}

export default function MediaGroupForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  title,
  subtitle = 'Fill out this form to make applying for media credentials at events super fast.',
  submitLabel,
  cancelPath = '/profile',
  currentLogoUrl = null,
}: MediaGroupFormProps) {
  const setParticipant = (fields: Partial<MediaGroupFormData['participant']>) =>
    setData('participant', { ...data.participant, ...fields })

  const setMediaGroup = (fields: Partial<MediaGroupFormData['media_group']>) =>
    setData('media_group', { ...data.media_group, ...fields })

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        {subtitle}
      </Text>
      <Separator my="3" size="4" />
      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}
      <form onSubmit={onSubmit}>
        <Grid columns="1" gap="5" my="5">
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Name *</Text>
                <TextField.Root
                  id="name"
                  size="3"
                  value={data.participant.name}
                  onChange={(e) => setParticipant({ name: e.target.value })}
                  required
                  placeholder="Full name"
                >
                  <TextField.Slot>
                    <User height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Location</Text>
                <TextField.Root
                  id="location"
                  size="3"
                  value={data.participant.location}
                  onChange={(e) => setParticipant({ location: e.target.value })}
                  placeholder="Where are you (ish?)"
                >
                  <TextField.Slot>
                    <MapPinHouse height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Email</Text>
                <TextField.Root
                  type="email"
                  size="3"
                  id="email"
                  value={data.participant.email}
                  onChange={(e) => setParticipant({ email: e.target.value })}
                  placeholder="Better email to reach you at than your primary?"
                >
                  <TextField.Slot>
                    <Mail height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Phone Number</Text>
                <TextField.Root
                  type="text"
                  size="3"
                  id="phone_number"
                  value={data.participant.phone_number}
                  onChange={(e) => setParticipant({ phone_number: e.target.value })}
                  placeholder="Sometimes it's easier to just call you"
                >
                  <TextField.Slot>
                    <Phone height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
        </Grid>

        <Text size="6">Media Presence</Text>
        <Separator my="3" size="4" />
        <Grid columns={{ initial: '1', sm: '2' }} gap="5" my="5">
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Primary Outlet</Text>
                <Select.Root
                  size="3"
                  value={data.media_group.primary_outlet}
                  onValueChange={(e) => setMediaGroup({ primary_outlet: e })}
                >
                  <Select.Trigger id="primary_outlet" placeholder="Select your main platform..." />
                  <Select.Content>
                    <Select.Item value="instagram">Instagram</Select.Item>
                    <Select.Item value="youtube">YouTube</Select.Item>
                    <Select.Item value="flickr">Flickr</Select.Item>
                    <Select.Item value="website">Personal Website</Select.Item>
                    <Select.Item value="not_applicable">Not applicable</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Website</Text>
                <TextField.Root
                  type="url"
                  size="3"
                  id="portfolio_link"
                  value={data.media_group.portfolio_link}
                  onChange={(e) => setMediaGroup({ portfolio_link: e.target.value })}
                  placeholder="https://..."
                >
                  <TextField.Slot>
                    <Globe height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">List me under *</Text>
                <Select.Root
                  size="3"
                  value={data.media_group.listing}
                  onValueChange={(e) => setMediaGroup({ listing: e })}
                  required
                >
                  <Select.Trigger id="listing" />
                  <Select.Content>
                    <Select.Item value="photography">Photography</Select.Item>
                    <Select.Item value="drone">Drone</Select.Item>
                    <Select.Item value="unlisted">Don't list me</Select.Item>
                  </Select.Content>
                </Select.Root>
                <Text size="1" color="gray">
                  Which section you appear under on an event's media list. Pick "Don't list me" if you're
                  there as support rather than shooting.
                </Text>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Instagram</Text>
                <TextField.Root
                  size="3"
                  id="instagram"
                  value={data.participant.instagram}
                  onChange={(e) => setParticipant({ instagram: e.target.value })}
                  placeholder="@handle"
                >
                  <TextField.Slot>
                    <Instagram height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">YouTube</Text>
                <TextField.Root
                  size="3"
                  id="youtube"
                  value={data.participant.youtube}
                  onChange={(e) => setParticipant({ youtube: e.target.value })}
                  placeholder="Channel name or URL"
                >
                  <TextField.Slot>
                    <Youtube height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Flickr</Text>
                <TextField.Root
                  size="3"
                  id="flickr"
                  value={data.participant.flickr}
                  onChange={(e) => setParticipant({ flickr: e.target.value })}
                  placeholder="Username or URL"
                >
                  <TextField.Slot>
                    <Camera height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Facebook</Text>
                <TextField.Root
                  size="3"
                  id="facebook"
                  value={data.participant.facebook}
                  onChange={(e) => setParticipant({ facebook: e.target.value })}
                  placeholder="Page name or URL"
                >
                  <TextField.Slot>
                    <Facebook height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">TikTok</Text>
                <TextField.Root
                  size="3"
                  id="tiktok"
                  value={data.participant.tiktok}
                  onChange={(e) => setParticipant({ tiktok: e.target.value })}
                  placeholder="@handle"
                >
                  <TextField.Slot>
                    <Music2 height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
          <Box>
            <Text as="label" size="3">
              <Flex gap="2" direction="column">
                <Text size="5">Threads</Text>
                <TextField.Root
                  size="3"
                  id="threads"
                  value={data.participant.threads}
                  onChange={(e) => setParticipant({ threads: e.target.value })}
                  placeholder="@handle"
                >
                  <TextField.Slot>
                    <AtSign height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Text>
          </Box>
        </Grid>

        <Text size="6">Logo</Text>
        <Separator my="3" size="4" />
        <Flex direction="column" gap="2" my="5">
          {currentLogoUrl && (
            <Flex align="center" gap="3">
              <img
                src={currentLogoUrl}
                alt="Current logo"
                style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }}
              />
              <Text size="2" color="gray">Current logo — upload a new file to replace.</Text>
            </Flex>
          )}
          <Flex align="center" gap="2">
            <Image height="16" width="16" color="var(--gray-9)" />
            <input
              type="file"
              accept="image/*"
              style={{ fontSize: 'var(--font-size-2)' }}
              onChange={(e) => setMediaGroup({ logo: e.target.files?.[0] ?? null })}
            />
          </Flex>
          <Text size="1" color="gray">Square image recommended (PNG, JPG, or SVG).</Text>
        </Flex>

        <Box mt="8">
          <Flex gap="4" justify="end" dir="row">
            {cancelPath && (
              <Button type="button" size="3" asChild color="gray" variant="soft">
                <Link href={cancelPath}>Cancel</Link>
              </Button>
            )}
            <Button size="3" type="submit" disabled={processing}>
              {submitLabel}
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  )
}
